import React, { useEffect, useRef } from "react";
import { NavLink, Navigate, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Building2, KeyRound, FolderSearch, ShieldAlert, ScrollText, Settings, LogOut, Loader2, Receipt, Link2, Church } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/PublicShell";

const IDLE_MS = 15 * 60 * 1000;
const LINKS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, id: "admin-sidebar-overview-link", end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, id: "admin-sidebar-orders-link" },
  { to: "/admin/cases", label: "Case Queue", icon: FolderSearch, id: "admin-sidebar-cases-link" },
  { to: "/admin/organizations", label: "Organizations", icon: Building2, id: "admin-sidebar-orgs-link" },
  { to: "/admin/links", label: "Application Links", icon: Link2, id: "admin-sidebar-links-link" },
  { to: "/admin/codes", label: "Code Inventory", icon: KeyRound, id: "admin-sidebar-inventory-link" },
  { to: "/admin/billing", label: "Billing", icon: Receipt, id: "admin-sidebar-billing-link" },
  { to: "/admin/dow", label: "DOW Dashboard", icon: Church, id: "admin-sidebar-dow-link" },
  { to: "/admin/alerts", label: "Security Alerts", icon: ShieldAlert, id: "admin-sidebar-alerts-link" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, id: "admin-sidebar-audit-link" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "admin-sidebar-settings-link" },
];

function useIdleLogout(logout: () => Promise<void>) {
  const timer = useRef<any>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const reset = () => {
      clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        await logout();
        toast.info("You were signed out after 15 minutes of inactivity.");
        navigate("/admin/login");
      }, IDLE_MS);
    };
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer.current);
      evts.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [logout, navigate]);
}

function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const wide = useLocation().pathname.startsWith("/admin/dow");
  useIdleLogout(logout);
  if (!user) return null;
  const links = user.role === "dow_reviewer" ? LINKS.filter((l) => l.to === "/admin/dow") : LINKS;
  return (
    <div className="flex min-h-screen bg-[#F9F8F5]">
      <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col bg-[#1B365D] px-3 py-5 lg:flex">
        <div className="px-2"><BrandMark light /></div>
        <nav className="mt-8 flex-1 space-y-0.5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.id}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-semibold transition-colors duration-200 ${isActive ? "bg-white text-[#1B365D] shadow-sm" : "text-white/75 hover:bg-white/10 hover:text-white"}`
              }
            >
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="rounded-xl bg-white/5 p-3">
          <p className="truncate text-sm font-semibold text-white" data-testid="admin-current-user">{user.name}</p>
          <p className="truncate text-xs text-white/60">{user.email}</p>
          <span className="mt-2 inline-block rounded-full bg-[#F5C77E] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#1B365D]" data-testid="admin-current-role">{user.role}</span>
          <button
            data-testid="admin-change-password-link"
            onClick={() => navigate("/admin/account")}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
          >
            <KeyRound className="h-4 w-4" /> Change my password
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="sticky top-0 z-40 flex gap-1 overflow-x-auto border-b border-slate-200 bg-[#1B365D] px-3 py-2 lg:hidden">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold ${isActive ? "bg-white text-[#1B365D]" : "text-white/80"}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </div>
        <main className={`mx-auto ${wide ? "max-w-[1680px]" : "max-w-7xl"} px-4 py-8 sm:px-6 lg:px-10 lg:py-10`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="grid min-h-screen place-items-center bg-[#F9F8F5]"><Loader2 className="h-8 w-8 animate-spin text-[#1B365D]" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (user.role === "dow_reviewer" && !/^\/admin\/(dow|account)/.test(loc.pathname)) return <Navigate to="/admin/dow" replace />;
  return <Shell />;
}
